import {
  IVisitSalesItemPublicModel,
  VisitSalesItemPublicModel,
} from '@common/models';
import { defaultTo, max, min, sum } from 'lodash';
import { formatCurrency } from './numberUtils';

const estimateGivenPrice = (
  quantity: number,
  item: IVisitSalesItemPublicModel,
) => {
  if (item.salesItemOptionAllowUserEnteredPrice)
    return quantity * defaultTo(item.salesItemUnitPrice, 0);
  //rules apply just for visit sale item
  if (item instanceof VisitSalesItemPublicModel) {
    if (item.wasPreviouslyPended || item.salesPackageId) return 0;
    if (item.isPackageAddOn)
      return (
        quantity *
          defaultTo(item.packageAddOnPrice, getPackageAddOnPrice(item)) +
        (quantity > 0 ? defaultTo(item.dispensingFee, 0) : 0)
      );
    if (item.isPackageUpgrade)
      return (
        (!_.isNil(item.salesItemUnitPrice) &&
        !_.isNil(item.upgradingPrice) &&
        item.salesItemUnitPrice - item.upgradingPrice > 0
          ? item.salesItemUnitPrice - item.upgradingPrice
          : 0 * quantity) + defaultTo(item.dispensingFee, 0)
      );

    return calculateDefaultUnitPrice(quantity, item);
  }
  // otherwise, prefer the unit price but default to the item price.
  return calculateDefaultUnitPrice(quantity, item);
};

const calculateDefaultUnitPrice = (
  quantity: number,
  item: IVisitSalesItemPublicModel,
) => {
  return (
    quantity * defaultTo(item.salesItemUnitPrice, item.salesItemPrice) +
    (quantity > 0 ? defaultTo(item.dispensingFee, 0) : 0)
  );
};

const getPackageAddOnPrice = (item: IVisitSalesItemPublicModel) => {
  return (
    (item.salesItemPrice - defaultTo(item.dispensingFee, 0)) /
    defaultTo(item.quantity, 1)
  );
};

const estimatePrice = (
  vsi: IVisitSalesItemPublicModel,
  estimatedQuantity: number | undefined,
) => {
  // if this item was declined by either the client or the vet, it will have a zero dollar amount.
  if (vsi.wasDeclinedByClient || vsi.wasDeclinedByVeterinarian) return 0;
  // if there is an estimated quantity, base the pricing on the estimate and unit price.
  // Include the dispensing fee if present.
  return estimateGivenPrice(
    defaultTo(estimatedQuantity, defaultTo(vsi.quantity, 1)),
    vsi,
  );
};

export const calculateEstimateSubtotals = (
  packagePrice: number | undefined,
  items: IVisitSalesItemPublicModel[],
) => {
  const subMin = sum(items.map((i) => estimatePrice(i, i.estimateQuantityLow)));
  const subMax = sum(
    items.map((i) => estimatePrice(i, i.estimateQuantityHigh)),
  );
  return {
    subtotalMin: min([subMin, subMax])! + defaultTo(packagePrice, 0),
    subtotalMax: max([subMin, subMax])! + defaultTo(packagePrice, 0),
  };
};

export const formatEstimateItemQuantity = (
  item: IVisitSalesItemPublicModel,
) => {
  // if the item was declined, then there will be no quantity.
  if (item.wasDeclinedByClient) return `0`;

  if (
    item.estimateQuantityLow !== undefined &&
    item.estimateQuantityHigh !== undefined
  ) {
    return item.estimateQuantityLow === item.estimateQuantityHigh
      ? `${item.estimateQuantityLow}`
      : `${item.estimateQuantityLow}-${item.estimateQuantityHigh}`;
  } else if (item.estimateQuantityLow !== undefined)
    return `${item.estimateQuantityLow}`;
  else if (item.estimateQuantityHigh !== undefined)
    return `${item.estimateQuantityHigh}`;
  else return `${defaultTo(item.quantity, 1)}`;
};

export const formatEstimateItemAmount = (item: IVisitSalesItemPublicModel) => {
  // if the item was declined, then there will be no quantity.
  if (item.wasDeclinedByClient) return `$0.00`;

  const priceQuantity = (estimateQty: number) =>
    formatCurrency(estimateGivenPrice(estimateQty, item));

  if (
    item.estimateQuantityLow !== undefined &&
    item.estimateQuantityHigh !== undefined
  ) {
    return item.estimateQuantityLow === item.estimateQuantityHigh
      ? `${priceQuantity(item.estimateQuantityLow)}`
      : `${priceQuantity(item.estimateQuantityLow)} - ${priceQuantity(item.estimateQuantityHigh)}`;
  } else if (item.estimateQuantityLow !== undefined)
    return `${priceQuantity(item.estimateQuantityLow)}`;
  else if (item.estimateQuantityHigh !== undefined)
    return `${priceQuantity(item.estimateQuantityHigh)}`;
  else return `${priceQuantity(defaultTo(item.quantity, 1))}`;
};
